import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { distinctUntilChanged } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Steps } from '../../../shared/components/steps/steps';
import { CardConteudo } from '../../../shared/components/card-conteudo/card-conteudo';
import { applyFormat, VivestControl } from '../../../core/directives/vivest-control';
import { BancoApi, Cadastro, ContaCadastrada, PaisApi } from '../../../core/models/requerimento';
import { toCadastroPayload } from '../../../core/models/requerimento-mapper';
import { cepValidator } from '../../../core/validators/cep-validator';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import {
  distribuirErrosDaApi,
  errosNosControles,
  mensagemDoServidor,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

const MENSAGEM_ERRO_BASE = 'Não foi possível carregar seus dados cadastrais, bancos e países. Tente novamente.';
const MENSAGEM_REVISAR_BASE = 'Os dados do cadastro mudaram na base unificada. Revise as informações antes de continuar.';

/** Bancos da base unificada como opções do select ("001 - BCO DO BRASIL S.A."), em ordem de código. */
function paraBancoOptions(bancos: BancoApi[]): VivestSelectOption[] {
  return [...bancos]
    .sort((a, b) => a.codigo - b.codigo)
    .map(({ codigo, nome }) => {
      const valor = String(codigo).padStart(3, '0');
      return { value: valor, label: `${valor} - ${nome}` };
    });
}

/** Países da base unificada como opções do select (value = nome: a API compara com nom_pais_exterior), em ordem alfabética. */
function paraPaisOptions(paises: PaisApi[]): VivestSelectOption[] {
  return paises
    .map(({ nome }) => ({ value: nome, label: nome }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** O fluxo só aceita o Banco Santander: o campo banco fica travado nele. */
const CODIGO_BANCO_SANTANDER = 33;
const BANCO_SANTANDER = String(CODIGO_BANCO_SANTANDER).padStart(3, '0');

/** Só as contas do Santander, o único banco aceito pelo fluxo. */
function contasDoSantander(contas: ContaCadastrada[]): ContaCadastrada[] {
  return contas.filter(({ codBanco }) => codBanco === CODIGO_BANCO_SANTANDER);
}

/** Contas cadastradas como opções do select ("8716287-6 Banco Santander (Brasil) S.A."); value = posição na lista. */
function paraContaOptions(contas: ContaCadastrada[]): VivestSelectOption[] {
  return contas.map(({ conta, nomeBanco }, indice) => ({ value: String(indice), label: `${conta} ${nomeBanco}` }));
}

/** CEP da API (às vezes sem os zeros à esquerda, como 5861300) no formato 05861-300. */
function formatarCep(cep: string | null): string {
  const digitos = (cep ?? '').replace(/\D/g, '');
  return digitos ? applyFormat(digitos.padStart(8, '0'), 'cep') : '';
}

/** CPF da API (só dígitos, às vezes sem os zeros à esquerda) no formato 000.000.000-00. */
function formatarCpf(cpf: string): string {
  const digitos = cpf.replace(/\D/g, '');
  if (digitos === '') return '';
  return digitos.padStart(11, '0').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/** "2001-07-18T00:00:00" (API) para "18/07/2001". */
function formatarData(data: string | null): string {
  if (!data) return '';
  const [ano, mes, dia] = data.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

/** DDD de quem reside no Brasil: 11 a 99 (a base unificada recusa outro valor). No exterior o código de área é livre. */
function dddBrasil(resideExterior: () => boolean): ValidatorFn {
  return (control) => {
    const valor = String(control.value ?? '');
    if (valor === '' || resideExterior()) return null;
    const numero = Number(valor);
    return /^\d{2}$/.test(valor) && numero >= 11 && numero <= 99 ? null : { dddForaDaFaixa: true };
  };
}

type CampoFormulario =
  | 'email'
  | 'emailSecundario'
  | 'ddd'
  | 'celular'
  | 'cep'
  | 'uf'
  | 'cidade'
  | 'bairro'
  | 'logradouro'
  | 'numero'
  | 'complemento'
  | 'pais'
  | 'zipCode'
  | 'nif'
  | 'banco'
  | 'contaCadastrada'
  | 'agencia'
  | 'conta';

const CAMPO_DA_API: Record<string, CampoFormulario> = {
  dsEmailPrinc: 'email',
  dsEmailSec: 'emailSecundario',
  codDddCelular: 'ddd',
  numCelular: 'celular',
  codCep: 'cep',
  codUf: 'uf',
  nomCidade: 'cidade',
  nomBairro: 'bairro',
  dsEndereco: 'logradouro',
  numEndereco: 'numero',
  dsComplemento: 'complemento',
  nomPaisExterior: 'pais',
  codZipExterior: 'zipCode',
  numNifExterior: 'nif',
  codBanco: 'banco',
  codAgencia: 'agencia',
  numConta: 'conta',
  dsDigitoConta: 'conta',
};

const MENSAGEM_MOLESTIA_OBRIGATORIA = 'Informe se possui moléstia grave para continuar.';
const MENSAGEM_ERRO_SALVAR = 'Não foi possível salvar os dados. Tente novamente.';
const MENSAGEM_DDD_INVALIDO = 'Informe um DDD entre 11 e 99.';

/** Campos de endereço no Brasil: exibidos e validados só para quem não reside no exterior. */
const CAMPOS_ENDERECO = [
  'cep',
  'uf',
  'cidade',
  'bairro',
  'logradouro',
  'numero',
  'complemento',
] as const;

/** Campos exibidos e validados apenas quando o usuário reside no exterior. */
const CAMPOS_EXTERIOR = ['pais', 'zipCode', 'nif'] as const;

const MENSAGEM_CEP_INVALIDO = 'Informe um CEP válido.';
const MENSAGEM_CEP_FALHA = 'Não foi possível consultar o CEP. Tente novamente.';
const MENSAGEM_CEP_NAO_ENCONTRADO = 'CEP não encontrado. Preencha o endereço manualmente.';

/** Campos de endereço preenchidos pela consulta de CEP e desabilitados quando a API retorna valor. */
const CAMPOS_ENDERECO_CEP = ['uf', 'cidade', 'bairro', 'logradouro'] as const;

@Component({
  imports: [Steps, CardConteudo, ReactiveFormsModule, VivestControl],
  selector: 'app-dados-cadastrais-requerimento',
  styleUrl: './dados-cadastrais-requerimento.scss',
  templateUrl: './dados-cadastrais-requerimento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DadosCadastraisRequerimento implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(1);
  readonly totalSteps = signal(7);
  readonly resideExterior = signal(false);
  readonly possuiMolestiaGrave = signal<boolean | null>(null);
  /** Mensagem exibida no campo CEP quando a consulta falha ou o CEP não é encontrado. */
  readonly mensagemConsultaCep = signal('');

  protected readonly bancoOptions = signal<VivestSelectOption[]>([]);
  protected readonly paisOptions = signal<VivestSelectOption[]>([]);
  /** Contas do participante na base unificada (só as do Santander), da mais recente para a mais antiga. */
  protected readonly contasCadastradas = signal<ContaCadastrada[]>([]);
  protected readonly contaOptions = computed(() => paraContaOptions(this.contasCadastradas()));

  protected readonly form = this.formBuilder.nonNullable.group({
    // Só leitura: vêm do participante do GET /v1/requerimentos/home.
    nome: [{ value: '', disabled: true }],
    cpf: [{ value: '', disabled: true }],
    dataNascimento: [{ value: '', disabled: true }],
    email: ['', [Validators.required, Validators.email]],
    emailSecundario: ['', [Validators.email]],
    ddd: ['', [Validators.required, dddBrasil(() => this.resideExterior())]],
    celular: ['', [Validators.required]],
    ddd2: [''],
    celular2: [''],
    cep: ['', [Validators.required, cepValidator]],
    uf: ['', [Validators.required]],
    cidade: ['', [Validators.required]],
    bairro: ['', [Validators.required]],
    logradouro: ['', [Validators.required]],
    numero: ['', [Validators.required]],
    complemento: [''],
    pais: ['', [Validators.required]],
    zipCode: ['', [Validators.required]],
    nif: ['', [Validators.required]],
    // Travado no Santander, o único banco aceito; vai no PATCH pelo getRawValue.
    banco: [{ value: BANCO_SANTANDER, disabled: true }],
    // Obrigatória: preenche agência e conta. Não vai no PATCH (o montarCadastro não a lê).
    contaCadastrada: ['', [Validators.required]],
    agencia: ['', [Validators.required]],
    conta: ['', [Validators.required]],
  });

  private readonly statusFormulario = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  /** Libera o continuar com os campos obrigatórios exibidos preenchidos e a moléstia grave respondida. */
  protected readonly podeContinuar = computed(
    () => this.statusFormulario() === 'VALID' && this.possuiMolestiaGrave() !== null,
  );

  constructor() {
    // Enquanto o usuário digita um novo CEP, esconde o erro anterior; ele volta a ser avaliado no blur.
    this.form.controls.cep.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.mensagemConsultaCep.set('');
      this.form.controls.cep.markAsUntouched();
    });

    // As contas chegam com a base, depois do preenchimento: ao habilitar o select, o enable() reemite a conta já
    // escolhida, que não pode sobrescrever a agência e a conta salvas.
    this.form.controls.contaCadastrada.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe((valor) => {
      const conta = this.contasCadastradas()[Number(valor)];
      if (valor !== '' && conta) {
        this.selecionarContaCadastrada(conta);
      }
    });

    // Sem conta cadastrada a oferecer, o select some e deixa de ser obrigatório.
    effect(() => this.alternarCampo('contaCadastrada', this.contaOptions().length > 0));

    // Desabilitado, o campo é ignorado na validação; assim só os campos exibidos são obrigatórios.
    effect(() => {
      const resideExterior = this.resideExterior();

      CAMPOS_ENDERECO.forEach((campo) => this.alternarCampo(campo, !resideExterior));
      CAMPOS_EXTERIOR.forEach((campo) => this.alternarCampo(campo, resideExterior));
      // De volta ao Brasil, o endereço preenchido continua bloqueado, como depois da consulta de CEP.
      if (!resideExterior) {
        this.desabilitarCamposEnderecoPreenchidos();
      }
      this.form.controls.ddd.updateValueAndValidity();
    });
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    const participante = this.carregarParticipante();
    const base = this.carregarBase();
    // Vindo de um envio recusado por divergência na base: a tela mostra os dados atuais da base para revisão.
    const revisarBase = history.state?.revisarBase === true;
    try {
      const [, cadastroBase] = await Promise.all([participante, base]);
      const requerimento = await this.requerimentoState.carregar();
      // O cadastro já salvo no rascunho prevalece; sem ele, a tela sai com o da base para o participante revisar.
      const cadastro = revisarBase
        ? await this.recarregarDaBase(requerimento.id)
        : (requerimento.cadastro ?? cadastroBase);
      if (cadastro) {
        this.preencherFormulario(cadastro);
      }
    } catch (erro) {
      // 404: ainda não há requerimento (a home não devolveu um em andamento). O formulário sai com os dados da base
      // unificada e o requerimento é aberto no "Continuar"; nada é gravado até lá.
      if (erro instanceof HttpErrorResponse && erro.status === 404) {
        const cadastroBase = await base;
        if (cadastroBase) {
          this.preencherFormulario(cadastroBase);
        }
        return;
      }
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  onReturn() {
    this.router.navigate(['/requerimento-beneficio']);
  }

  /**
   * GET dados-cadastrais-base (sem gravar): carrega bancos, países e contas nos selects e devolve o cadastro vigente
   * para preencher a tela. A conta do cadastro só entra se for do Santander, o único banco do fluxo. Sem cadastro na
   * base (409) ou com ela fora do ar, a tela avisa, os selects ficam vazios e o participante preenche à mão.
   */
  private async carregarBase(): Promise<Cadastro | null> {
    try {
      const { cadastro, contas, bancos, paises } = await this.requerimentoService.obterDadosCadastraisBase();
      this.bancoOptions.set(paraBancoOptions(bancos));
      this.paisOptions.set(paraPaisOptions(paises));
      this.contasCadastradas.set(contasDoSantander(contas));
      return cadastro.codBanco === CODIGO_BANCO_SANTANDER ? cadastro : { ...cadastro, agencia: null, conta: null };
    } catch {
      this.toastService.show(MENSAGEM_ERRO_BASE, 'error');
      return null;
    }
  }

  /** Refaz o rascunho com a base unificada (substituir) e devolve o cadastro atual para revisão. */
  private async recarregarDaBase(id: number): Promise<Cadastro | null> {
    await this.requerimentoService.carregarDadosCadastrais(id, true);
    const atualizado = await this.requerimentoService.getPorId(id);
    this.requerimentoState.definir(atualizado);
    this.toastService.show(MENSAGEM_REVISAR_BASE, 'error', 6000);
    return atualizado.cadastro;
  }

  /**
   * Nome, CPF e data de nascimento (só leitura) do participante da home. Sem a home, os campos ficam vazios e o
   * cadastro segue: eles não vão no PATCH.
   */
  private async carregarParticipante(): Promise<void> {
    try {
      const { participante } = await this.requerimentoState.carregarHome();
      this.form.patchValue({
        nome: participante.nome?.trim() ?? '',
        cpf: formatarCpf(participante.cpf),
        dataNascimento: formatarData(participante.dataNascimento),
      });
    } catch {
      // Os campos ficam vazios; os erros de acesso aparecem no carregamento do requerimento.
    }
  }

  async onSubmit() {
    this.form.markAllAsTouched();

    const molestiaGrave = this.possuiMolestiaGrave();
    if (molestiaGrave === null) {
      this.toastService.show(MENSAGEM_MOLESTIA_OBRIGATORIA, 'error');
    }
    if (this.form.invalid || molestiaGrave === null) {
      return;
    }

    const idTpInst = environment.requerimentoTipoInstitutoId;
    let requerimento = this.requerimentoState.requerimento();
    if (!requerimento && idTpInst === null) {
      this.router.navigate([ROTA_PAGINA_INDISPONIVEL]);
      return;
    }

    this.loaderService.showLoader('Salvando...');
    try {
      if (!requerimento) {
        // Primeira etapa sem requerimento: abre o rascunho (POST) e guarda no estado antes de salvar o cadastro. Se o
        // PATCH falhar, a nova tentativa já encontra o requerimento e não abre outro.
        requerimento = await this.requerimentoService.abrir(idTpInst!, environment.idBeneficioAposentadoria);
        this.requerimentoState.definir(requerimento);
      }

      // Origem do envio: a base é lida uma vez, na primeira carga (antes de o cadastro ser gravado). Depois disso a
      // origem fica como está, porque é com ela que o envio detecta mudanças; a revisão após o 409 refaz a origem.
      if (!requerimento.cadastro?.dtOrigemCadastro) {
        await this.requerimentoService.carregarDadosCadastrais(requerimento.id);
      }
      const atualizado = await this.requerimentoService.salvar(requerimento.id, {
        cadastro: toCadastroPayload(this.montarCadastro(molestiaGrave)),
      });
      this.requerimentoState.definir(atualizado);
      this.router.navigate(['/requerimento-beneficio/configuracao-beneficio']);
    } catch (erro) {
      this.tratarErroAoSalvar(erro);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Vermelho com erro da API ou, depois de a pessoa sair do campo, obrigatório vazio ou inválido. */
  protected status(campo: CampoFormulario): 'default' | 'error' {
    const control = this.form.controls[campo];
    return this.mensagemErro(campo) || (control.invalid && control.touched) ? 'error' : 'default';
  }

  protected mensagemErro(campo: CampoFormulario): string {
    const control = this.form.controls[campo];
    const mensagem = mensagemDoServidor(control);
    if (campo === 'ddd' && !mensagem && control.touched && control.hasError('dddForaDaFaixa')) {
      return MENSAGEM_DDD_INVALIDO;
    }
    if (mensagem || campo !== 'cep') return mensagem;

    if (this.mensagemConsultaCep()) return this.mensagemConsultaCep();
    return control.touched && control.hasError('cep') ? MENSAGEM_CEP_INVALIDO : '';
  }

  /** No blur do CEP: consulta o ViaCEP e preenche o endereço, bloqueando os campos que vierem com valor. */
  async searchCep(): Promise<void> {
    const cepControl = this.form.controls.cep;
    this.mensagemConsultaCep.set('');
    // Garante a exibição do erro de formato no blur, independentemente da ordem dos eventos blur/focusout.
    cepControl.markAsTouched();

    // Só consulta CEP válido; o erro de formato é exibido pelo validator.
    if (!cepControl.value || cepControl.invalid) {
      return;
    }

    this.loaderService.showLoader('Carregando...');
    this.habilitarCamposEndereco();
    try {
      const endereco = await this.requerimentoService.consultarCep(cepControl.value);
      // O ViaCEP responde 200 com { erro: true } quando o CEP não existe.
      if (!endereco || endereco.erro) {
        this.limparCamposEndereco();
        this.mensagemConsultaCep.set(MENSAGEM_CEP_NAO_ENCONTRADO);
      } else {
        this.form.patchValue({
          uf: endereco.uf ?? '',
          cidade: endereco.localidade ?? '',
          bairro: endereco.bairro ?? '',
          logradouro: endereco.logradouro ?? '',
          complemento: endereco.complemento ?? '',
        });
        this.desabilitarCamposEnderecoPreenchidos();
      }
    } catch {
      this.mensagemConsultaCep.set(MENSAGEM_CEP_FALHA);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private preencherFormulario(cadastro: Cadastro): void {
    this.form.patchValue({
      email: cadastro.emailPrincipal,
      emailSecundario: cadastro.emailSecundario ?? '',
      ddd: cadastro.dddCelular ?? '',
      celular: applyFormat(cadastro.celular ?? '', 'celular'),
      cep: formatarCep(cadastro.cep),
      uf: cadastro.uf ?? '',
      cidade: cadastro.cidade ?? '',
      bairro: cadastro.bairro ?? '',
      logradouro: cadastro.endereco ?? '',
      numero: cadastro.numeroEndereco ?? '',
      complemento: cadastro.complemento ?? '',
      pais: cadastro.paisExterior ?? '',
      zipCode: cadastro.zipExterior ?? '',
      nif: cadastro.nifExterior ?? '',
      // Antes de agência e conta: a escolha da conta preenche as duas, e logo depois entram os valores salvos.
      contaCadastrada: this.contaCadastradaSalva(cadastro),
      agencia: cadastro.agencia ?? '',
      conta: applyFormat(cadastro.conta ?? '', 'conta'),
    });
    // Mesmo comportamento da consulta de CEP: o que veio salvo fica bloqueado, o que veio vazio fica para o usuário.
    this.desabilitarCamposEnderecoPreenchidos();
    this.resideExterior.set(cadastro.resideExterior);
    this.possuiMolestiaGrave.set(cadastro.molestiaGrave);
  }

  private montarCadastro(molestiaGrave: boolean): Cadastro {
    const valores = this.form.getRawValue();
    const exterior = this.resideExterior();
    const noBrasil = (valor: string): string | null => (exterior ? null : valor);

    return {
      emailPrincipal: valores.email,
      emailSecundario: valores.emailSecundario,
      dddCelular: valores.ddd,
      celular: valores.celular,
      cep: noBrasil(valores.cep),
      uf: noBrasil(valores.uf),
      cidade: noBrasil(valores.cidade),
      bairro: noBrasil(valores.bairro),
      endereco: noBrasil(valores.logradouro),
      numeroEndereco: noBrasil(valores.numero),
      complemento: noBrasil(valores.complemento),
      resideExterior: exterior,
      paisExterior: valores.pais,
      zipExterior: valores.zipCode,
      nifExterior: valores.nif,
      codBanco: valores.banco ? Number(valores.banco) : null,
      agencia: valores.agencia,
      conta: valores.conta,
      molestiaGrave,
    };
  }

  private tratarErroAoSalvar(erro: unknown): void {
    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    const destinos = errosNosControles(this.form.controls, CAMPO_DA_API);
    distribuirErrosDaApi(erro, destinos, MENSAGEM_ERRO_SALVAR).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }

  /** Posição da conta cadastrada com a agência e a conta salvas (comparando só os dígitos); '' quando não há. */
  private contaCadastradaSalva(cadastro: Cadastro): string {
    const digitos = (valor: string | null) => (valor ?? '').replace(/\D/g, '');
    // A agência salva pode trazer o dígito (1234-X); a conta cadastrada não tem, então compara só o número.
    const numeroAgencia = (valor: string | null) => digitos((valor ?? '').split('-')[0]);
    const indice = this.contasCadastradas().findIndex(
      ({ agencia, conta }) =>
        numeroAgencia(agencia) === numeroAgencia(cadastro.agencia) && digitos(conta) === digitos(cadastro.conta),
    );
    return indice === -1 ? '' : String(indice);
  }

  /** Preenche agência e conta com a conta escolhida (sempre do Santander, o banco travado); continuam editáveis. */
  private selecionarContaCadastrada({ agencia, conta }: ContaCadastrada): void {
    this.form.patchValue({ agencia, conta: applyFormat(conta, 'conta') });
  }

  private habilitarCamposEndereco(): void {
    CAMPOS_ENDERECO_CEP.forEach((campo) => this.form.controls[campo].enable());
  }

  private limparCamposEndereco(): void {
    CAMPOS_ENDERECO_CEP.forEach((campo) => this.form.controls[campo].reset());
    this.form.controls.numero.reset();
    this.form.controls.complemento.reset();
  }

  /** Bloqueia a edição apenas dos campos que a API preencheu; os vazios ficam para o usuário completar. */
  private desabilitarCamposEnderecoPreenchidos(): void {
    CAMPOS_ENDERECO_CEP
      .filter((campo) => this.form.controls[campo].value)
      .forEach((campo) => this.form.controls[campo].disable());
  }

  private alternarCampo(campo: keyof typeof this.form.controls, habilitado: boolean): void {
    const control = this.form.controls[campo];

    if (habilitado) {
      control.enable();
    } else {
      control.disable();
    }
  }
}
